// 非空間データテーブルの購読と操作（取込・削除）。
//
// 役割: App.jsx から切り出したデータ結線フック。DataStore の単一インスタンスを所有し、
//       購読スナップショット（dataTables）と各操作ハンドラを返す。DuckDB 本体の操作は
//       data/data-service へ委譲する。地図には載せない（レイヤーと分離）。
// 注入: { log }（App が握る実行ログの共有点）。
import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from 'react'

import { DataStore } from '../data/data-store'
import { importCsvDataTable, dropDataTable } from '../data/data-service'
import {
  loadSessionTables,
  removeSessionTable,
  restoreSessionTable,
  saveSessionTable,
} from '../data/session-storage'

// モジュールスコープの単一インスタンス（データタブ状態の単一の真実）。
const dataStore = new DataStore()

export function useDataActions({ log, ready = false }) {
  const dataTables = useSyncExternalStore(dataStore.subscribe, dataStore.getSnapshot)
  const [busy, setBusy] = useState(false)
  const restoredRef = useRef(false)

  useEffect(() => {
    if (!ready || restoredRef.current) return
    restoredRef.current = true
    let cancelled = false
    async function restore() {
      const records = await loadSessionTables('data')
      for (const record of records) {
        if (cancelled) return
        try {
          await restoreSessionTable(record)
          dataStore.add(record.meta)
        } catch (e) {
          log(`データ復元エラー: ${record.id}: ${String(e?.message ?? e)}`)
        }
      }
    }
    restore()
    return () => {
      cancelled = true
    }
  }, [ready, log])

  // CSV を順に取り込み、データテーブルとして登録する。非 CSV は弾く（データタブは CSV 専用）。
  const handleDataFiles = useCallback(
    async (files) => {
      setBusy(true)
      try {
        for (const file of files) {
          if (!/\.csv$/i.test(file.name)) {
            log(`データ取込スキップ（CSV のみ対応）: ${file.name}`)
            continue
          }
          try {
            const table = await importCsvDataTable(file)
            dataStore.add(table)
            void saveSessionTable('data', table.tableId, table)
            log(`データ取込: ${table.displayName}（${table.columns.length} 列・${table.rowCount} 行）`)
          } catch (e) {
            log(`データ取込エラー: ${file.name}: ${String(e?.message ?? e)}`)
          }
        }
      } finally {
        setBusy(false)
      }
    },
    [log],
  )

  const handleRemoveDataTable = useCallback(
    async (tableId) => {
      const removed = dataStore.remove(tableId)
      if (!removed) return
      try {
        await dropDataTable(tableId)
        await removeSessionTable('data', tableId)
        log(`データ削除: ${removed.displayName}`)
      } catch (e) {
        log(`データ削除エラー: ${String(e?.message ?? e)}`)
      }
    },
    [log],
  )

  const clearDataTables = useCallback(async () => {
    const current = dataStore.getSnapshot()
    await Promise.all(current.map((table) => dropDataTable(table.tableId).catch(() => {})))
    dataStore.clear()
  }, [])

  return { dataTables, busy, handleDataFiles, handleRemoveDataTable, clearDataTables }
}
